import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger as ChakraPopoverTrigger,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { FaceSad, Globe05 } from '@kula-ai/icons-core';

const PopoverTrigger: React.FC<{ children: React.ReactNode }> =
  ChakraPopoverTrigger;

interface Props {
  onSetTimezone?: () => void;
}

// `mouseDown` is what react-select uses to toggle its menu. Every
// interactive element inside the chip's portaled popover must stop both
// `mouseDown` and `click`, otherwise the AsyncSelect dropdown opens
// behind the popover.
const stop = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

export const InterviewerTimezoneTrigger = ({ onSetTimezone }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="left"
      trigger="hover"
      isLazy
    >
      <PopoverTrigger>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          cursor="pointer"
          color="secondary-700"
          _hover={{ color: 'secondary-900' }}
          onMouseEnter={onOpen}
          onMouseDown={stop}
          onClick={stop}
          aria-label="Interviewer's timezone not set"
        >
          <Globe05 size={16} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="base-white"
          borderRadius="8px"
          border="none"
          p="0"
          w="380px"
          zIndex="popover"
          boxShadow="0px 71px 29px rgba(215, 218, 235, 0.01), 0px 40px 24px rgba(215, 218, 235, 0.05), 0px 18px 18px rgba(215, 218, 235, 0.09), 0px 4px 10px rgba(215, 218, 235, 0.1)"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onMouseDown={stop}
          onClick={stop}
        >
          <Flex flexDir="column" p="3" gap="2">
            <Flex gap="1.5" alignItems="center">
              <Icon
                w="20px"
                h="20px"
                // @ts-expect-error react-icons type mismatch with Chakra Icon
                as={FaceSad}
                color="secondary-700"
                flexShrink={0}
              />
              <Text variant="p" color="heading" className="font-medium">
                Interviewer&apos;s timezone not available
              </Text>
            </Flex>
            <Text variant="p" color="muted">
              This interviewer hasn&apos;t set their timezone.
              <br />
              Candidates can&apos;t see their availability without it.
            </Text>
            <Button
              width="fit-content"
              variant="tertiary"
              onMouseDown={stop}
              onClick={(e: React.MouseEvent) => {
                stop(e);
                onClose();
                // 200ms — long enough for the Chakra Popover's
                // fade-out animation to finish before the kula-ai
                // Modal opens, otherwise both overlay for a frame and
                // it reads as a glitch.
                setTimeout(() => onSetTimezone?.(), 200);
              }}
            >
              Set timezone
            </Button>
          </Flex>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
